import React from 'react';
import { Loader2 } from 'lucide-react';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success' | 'outline';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  className?: string;
}

const BASE =
  'inline-flex items-center justify-center font-medium transition-all cursor-pointer outline-none disabled:cursor-not-allowed';

const VARIANT_STYLES: Record<ButtonVariant, { normal: string; disabled: string }> = {
  primary: {
    normal:
      'bg-gradient-to-r from-brand to-[#7c3aed] text-content-inverse hover:shadow-lg hover:shadow-brand/20 hover:-translate-y-px',
    disabled: 'bg-surface-primary text-content-faint',
  },
  secondary: {
    normal:
      'border border-subtle text-content-secondary hover:text-content hover:bg-surface-primary hover:border-default',
    disabled: 'border border-subtle text-content-faint bg-transparent',
  },
  ghost: {
    normal:
      'text-content-tertiary hover:text-content-secondary hover:bg-surface-primary',
    disabled: 'text-content-faint bg-transparent',
  },
  danger: {
    normal:
      'bg-red-500/20 text-red-400 hover:bg-red-500/30',
    disabled: 'bg-surface-primary text-content-faint',
  },
  success: {
    normal:
      'bg-status-success/10 text-status-success border border-status-success/20 hover:bg-status-success/20',
    disabled: 'bg-surface-primary text-content-faint border border-subtle',
  },
  outline: {
    normal:
      'border border-default text-content hover:bg-surface-primary',
    disabled: 'border border-subtle text-content-faint',
  },
};

const SIZE_STYLES: Record<ButtonSize, string> = {
  sm: 'px-2.5 py-1.5 rounded-lg text-2xs gap-1.5',
  md: 'px-3 py-2 rounded-lg text-xs gap-2',
  lg: 'px-5 py-2.5 rounded-lg text-sm gap-2',
};

const FOCUS_RING = 'focus-visible:ring-2 focus-visible:ring-brand/50 focus-visible:ring-offset-1 focus-visible:ring-offset-surface-page';

const LOADER_SIZE: Record<ButtonSize, number> = { sm: 12, md: 14, lg: 16 };

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      disabled = false,
      leftIcon,
      rightIcon,
      className = '',
      children,
      ...rest
    },
    ref,
  ) => {
    const isDisabled = disabled || loading;
    const vs = VARIANT_STYLES[variant];

    const classes = [
      BASE,
      SIZE_STYLES[size],
      FOCUS_RING,
      isDisabled ? vs.disabled : vs.normal,
      className,
    ].join(' ');

    return (
      <button
        ref={ref}
        className={classes}
        disabled={isDisabled}
        aria-busy={loading || undefined}
        {...rest}
      >
        {loading ? (
          <Loader2 size={LOADER_SIZE[size]} className="animate-spin" />
        ) : (
          leftIcon
        )}
        {children}
        {!loading && rightIcon}
      </button>
    );
  },
);

Button.displayName = 'Button';

export default Button;
export type { ButtonProps, ButtonVariant, ButtonSize };
